// "Stores" — group view only. Opportunity by store, ranked by total
// identified $; each row cross-filters the whole room to that store.

import { cx } from "@/lib/utils/cx"
import { ChapterSection, Highlight } from "../room-chrome"
import { CAT, FOAM, MINT_PALE, fmt$ } from "../room-model"
import type { HighlightAction, HighlightFinding, deriveStores } from "../derive"

type Stores = ReturnType<typeof deriveStores>

export function StoresChapter({ stores, storeId, onToggleStore, highlight, onHighlight }: {
  stores: Stores
  storeId: string
  onToggleStore: (id: string) => void
  highlight?: HighlightFinding
  onHighlight: (a: HighlightAction) => void
}) {
  return (
    <ChapterSection id="stores" eyebrow="Network" title="Opportunity by store" audience="For leadership">
      <div className="animate-element rounded-2xl border border-[#ECEBE6] bg-white overflow-hidden">
        <div className="px-6 pt-5 pb-3">
          <p className="text-sm font-semibold text-zinc-900">Ranked by total opportunity</p>
          <p className="text-xs text-[#8B897E] mt-0.5 mb-2">Click a store to filter the room</p>
          {highlight && <Highlight text={highlight.text} onClick={highlight.action ? () => onHighlight(highlight.action!) : undefined} />}
        </div>
        <div>
          {stores.rows.map(s => {
            const selected = storeId === s.id
            return (
              <button key={s.id} onClick={() => onToggleStore(s.id)}
                className={cx(
                  "w-full flex items-center gap-4 px-6 py-3 border-t border-[#ECEBE6] text-left transition-colors",
                  selected ? "bg-[#E8F8F2]" : "hover:bg-[#F7F6F1]",
                  storeId && !selected ? "opacity-45" : "",
                )}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-3 mb-1.5">
                    <p className="text-sm font-medium text-zinc-900 truncate">{s.name}</p>
                    <p className="text-xs text-[#8B897E] tabular-nums shrink-0">
                      {s.count} est · <span className={s.activeAdvisors7d === 0 ? "text-amber-600 font-medium" : ""}>{s.activeAdvisors7d} advisor{s.activeAdvisors7d !== 1 ? "s" : ""} active this wk</span>
                      {s.aging > 0 && <> · <span className="text-amber-600 font-medium">{s.aging} aging</span></>}
                    </p>
                  </div>
                  <div className="h-1.5 rounded-full bg-[#F1F0EB] overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${(s.identified / stores.max) * 100}%`, background: FOAM, border: `1px solid ${MINT_PALE}` }}>
                      <div className="h-full rounded-full" style={{ width: s.identified ? `${(s.converted / s.identified) * 100}%` : 0, background: CAT.brand }} />
                    </div>
                  </div>
                </div>
                <div className="text-right shrink-0 w-24">
                  <p className="text-sm font-semibold tabular-nums text-zinc-900">{fmt$(s.identified)}</p>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-[#8B897E]">identified</p>
                </div>
              </button>
            )
          })}
        </div>
      </div>
    </ChapterSection>
  )
}
